'use client';

// Browser side of passkeys: asks the device (fingerprint, face, PIN, or a phone nearby) to make
// or use a passkey, and sends the result to the server as base64url strings.

const toBytes = (b64u: string) => {
  const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
};
const toB64u = (buf: ArrayBuffer | ArrayBufferView) => {
  const bytes =
    buf instanceof ArrayBuffer
      ? new Uint8Array(buf)
      : new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const passkeysSupported = () =>
  typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function';

export type PasskeySetupOptions = {
  challenge: string;
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
};

/** Makes a new passkey on this device (or a phone nearby). */
export async function createPasskey(opts: PasskeySetupOptions) {
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge: toBytes(opts.challenge),
      rp: opts.rp,
      user: { id: toBytes(opts.user.id), name: opts.user.name, displayName: opts.user.displayName },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -8 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: { residentKey: 'preferred', userVerification: 'required' },
      attestation: 'none',
      timeout: 120_000,
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('No passkey was made');
  const r = cred.response as AuthenticatorAttestationResponse;
  if (typeof r.getPublicKey !== 'function' || typeof r.getAuthenticatorData !== 'function')
    throw new Error('This browser is too old for passkeys. Use the authenticator app instead.');
  const publicKey = r.getPublicKey();
  if (!publicKey) throw new Error('This device made a key type we cannot use');
  return {
    credentialId: toB64u(cred.rawId),
    clientDataJSON: toB64u(r.clientDataJSON),
    authenticatorData: toB64u(r.getAuthenticatorData()),
    publicKey: toB64u(publicKey),
    publicKeyAlgorithm: r.getPublicKeyAlgorithm(),
  };
}

export type PasskeySignInOptions = { challenge: string; rpId: string; allowCredentials: string[] };

/** Signs in with an existing passkey. */
export async function signWithPasskey(opts: PasskeySignInOptions) {
  const cred = (await navigator.credentials.get({
    publicKey: {
      challenge: toBytes(opts.challenge),
      rpId: opts.rpId,
      allowCredentials: opts.allowCredentials.map((id) => ({
        type: 'public-key',
        id: toBytes(id),
      })),
      userVerification: 'required',
      timeout: 120_000,
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('No passkey was used');
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    credentialId: toB64u(cred.rawId),
    clientDataJSON: toB64u(r.clientDataJSON),
    authenticatorData: toB64u(r.authenticatorData),
    signature: toB64u(r.signature),
  };
}

/** A short, friendly message for the errors browsers raise when the user cancels or times out. */
export const passkeyErrorMessage = (e: unknown) => {
  const name = (e as { name?: string })?.name;
  if (name === 'NotAllowedError')
    return 'The passkey request was cancelled or timed out. Try again.';
  if (name === 'InvalidStateError') return 'This device already has a passkey for this account.';
  if (name === 'SecurityError') return 'Passkeys only work on the SACCO website address (https).';
  return e instanceof Error ? e.message : String(e);
};
